// WebSocket-Client für den Raum-Worker.
//
// Handys kappen die Verbindung, sobald der Bildschirm ausgeht oder man die
// App wechselt. Der Client verbindet sich deshalb selbstständig neu und
// meldet sich mit dem gespeicherten Token wieder am selben Spielerplatz an.

export const ROOMS_BASE =
  process.env.REACT_APP_ROOMS_URL || "https://rooms.robin-link.de";

const WS_BASE = ROOMS_BASE.replace(/^http/, "ws");

// Host und Mitspieler bekommen getrennte Schlüssel. Sonst überschreiben sich
// zwei Tabs desselben Browsers gegenseitig die Sitzung — der Host verlor
// dabei seine Rolle und der Raum stand still.
const PLAYER_KEY = "musicQuizRoom";
const HOST_KEY = "musicQuizHostRoom";

// Welche Rolle dieser Tab spielt. sessionStorage ist pro Tab — nur so
// können Host und Mitspieler im selben Browser nebeneinander laufen,
// ohne sich die Sitzung gegenseitig wegzunehmen.
const ROLE_KEY = "musicQuizRole";

const keyFor = (role) => (role === "host" ? HOST_KEY : PLAYER_KEY);

// Herzschlag und Totmann: Nach Standby oder WLAN-Wechsel bleibt ein Socket
// oft scheinbar offen (readyState OPEN), obwohl nichts mehr durchgeht. Ein
// Buzz verpuffte dann still. Bleibt die Antwort auf ein Ping aus, wird die
// Verbindung verworfen und neu aufgebaut.
const PING_INTERVAL_MS = 25000;
const PONG_TIMEOUT_MS = 10000;
// Nach dem Aufwachen kuerzer: genau dann ist die Verbindung am ehesten tot,
// und genau dann will jemand buzzern.
const WAKE_PONG_TIMEOUT_MS = 4000;

const read = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key)) || null;
  } catch (e) {
    return null;
  }
};

/** Merkt sich Code, Rolle und Token, damit ein Reload nicht rauswirft. */
export function loadRoomSession(role) {
  if (role) return read(keyFor(role));

  const tabRole = sessionStorage.getItem(ROLE_KEY);
  if (tabRole) return read(keyFor(tabRole));

  // Frischer Tab ohne Rolle: Host hat Vorrang, er sitzt am steuernden Gerät.
  return read(HOST_KEY) || read(PLAYER_KEY);
}

export function saveRoomSession(session) {
  if (!session) return;
  sessionStorage.setItem(ROLE_KEY, session.role);
  localStorage.setItem(keyFor(session.role), JSON.stringify(session));
}

export function clearRoomSession(role) {
  if (role) {
    localStorage.removeItem(keyFor(role));
    if (sessionStorage.getItem(ROLE_KEY) === role) sessionStorage.removeItem(ROLE_KEY);
  } else {
    localStorage.removeItem(HOST_KEY);
    localStorage.removeItem(PLAYER_KEY);
    sessionStorage.removeItem(ROLE_KEY);
  }
}

export async function createRoom(settings) {
  const res = await fetch(`${ROOMS_BASE}/rooms`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ settings }),
  });
  if (!res.ok) throw new Error("room_create_failed");
  return res.json();
}

export async function fetchRoomInfo(code) {
  const res = await fetch(`${ROOMS_BASE}/rooms/${encodeURIComponent(code)}/info`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error("room_info_failed");
  return res.json();
}

export class RoomConnection {
  constructor({ code, token, onState, onMessage, onStatus, onGone }) {
    this.code = code;
    this.token = token || "";
    this.onState = onState || (() => { });
    this.onMessage = onMessage || (() => { });
    this.onStatus = onStatus || (() => { });
    // Raum existiert nicht mehr (abgelaufen oder aufgeräumt)
    this.onGone = onGone || (() => { });

    this.ws = null;
    this.closedByUs = false;
    this.attempt = 0;
    this.reconnectTimer = null;
    this.heartbeat = null;
    this.pongTimer = null;

    this._onWake = () => {
      if (document.visibilityState !== "visible") return;
      this.ensureConnected();
      // Steht der Socket scheinbar noch, pruefen ob er wirklich lebt.
      if (this.ws && this.ws.readyState === WebSocket.OPEN) this.probe(WAKE_PONG_TIMEOUT_MS);
    };
    document.addEventListener("visibilitychange", this._onWake);
    window.addEventListener("online", this._onWake);
    window.addEventListener("pageshow", this._onWake);
  }

  connect() {
    this.closedByUs = false;
    this.onStatus("connecting");

    const url = `${WS_BASE}/rooms/${encodeURIComponent(this.code)}/ws?token=${encodeURIComponent(this.token)}`;
    let ws;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      if (ws !== this.ws) return;
      this.attempt = 0;
      this.onStatus("connected");
      // Hält die Verbindung durch Zwischenstationen offen, die stille
      // Sockets nach kurzer Zeit kappen — und merkt, wenn sie tot ist.
      clearInterval(this.heartbeat);
      this.heartbeat = setInterval(() => this.probe(PONG_TIMEOUT_MS), PING_INTERVAL_MS);
    };

    ws.onmessage = (event) => {
      // Nachzuegler eines verworfenen Sockets nicht mehr verarbeiten
      if (ws !== this.ws) return;
      // Jede Nachricht beweist, dass die Verbindung lebt
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch (e) {
        return;
      }
      if (msg.room) this.onState(msg.room);
      this.onMessage(msg);
    };

    ws.onclose = () => {
      if (ws !== this.ws) return;
      clearInterval(this.heartbeat);
      clearTimeout(this.pongTimer);
      if (this.closedByUs) return;
      this.onStatus("reconnecting");
      this.scheduleReconnect();
    };

    ws.onerror = () => {
      // onclose folgt und übernimmt den Wiederaufbau
    };
  }

  /** Ping schicken; bleibt jede Antwort aus, gilt die Verbindung als tot. */
  probe(timeoutMs) {
    if (this.pongTimer) return; // laeuft schon eine Pruefung
    if (!this.send({ type: "ping", t: Date.now() })) return;
    this.pongTimer = setTimeout(() => {
      this.pongTimer = null;
      this.dropAndReconnect();
    }, timeoutMs);
  }

  /**
   * Toten Socket verwerfen und sofort neu verbinden. Nicht auf onclose
   * warten: bei einer halb offenen Verbindung kommt das erst nach langer
   * Zeit oder gar nicht.
   */
  dropAndReconnect() {
    if (this.closedByUs) return;
    const alt = this.ws;
    this.ws = null;
    clearInterval(this.heartbeat);
    clearTimeout(this.pongTimer);
    this.pongTimer = null;
    if (alt) {
      try {
        alt.close();
      } catch (e) { /* schon zu */ }
    }
    this.onStatus("reconnecting");
    clearTimeout(this.reconnectTimer);
    this.connect();
  }

  ensureConnected() {
    if (this.closedByUs) return;
    const state = this.ws && this.ws.readyState;
    if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) return;
    clearTimeout(this.reconnectTimer);
    this.connect();
  }

  scheduleReconnect() {
    clearTimeout(this.reconnectTimer);

    // Nach einigen Fehlversuchen nachsehen, ob der Raum überhaupt noch
    // existiert. Sonst würde ein abgelaufener Raum endlos angefunkt, ohne
    // dass jemand erfährt warum.
    if (this.attempt === 4) {
      fetchRoomInfo(this.code)
        .then((info) => {
          if (!info) {
            this.closedByUs = true;
            clearTimeout(this.reconnectTimer);
            clearInterval(this.heartbeat);
            this.onStatus("gone");
            this.onGone();
          }
        })
        .catch(() => { /* Netzproblem — weiter versuchen */ });
    }

    // Zügig beim ersten Versuch, danach zurückhaltender — aber nie länger
    // als 5 Sekunden, damit die Rückkehr aus dem Standby flott wirkt.
    const delay = Math.min(5000, 400 * Math.pow(2, this.attempt));
    this.attempt += 1;
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  send(payload) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(payload));
      return true;
    }
    return false;
  }

  /** Token nach dem Beitritt übernehmen, damit ein Reconnect greift. */
  setToken(token) {
    this.token = token || "";
  }

  close() {
    this.closedByUs = true;
    clearTimeout(this.reconnectTimer);
    clearInterval(this.heartbeat);
    clearTimeout(this.pongTimer);
    this.pongTimer = null;
    document.removeEventListener("visibilitychange", this._onWake);
    window.removeEventListener("online", this._onWake);
    window.removeEventListener("pageshow", this._onWake);
    if (this.ws) {
      try {
        this.ws.close();
      } catch (e) { /* schon zu */ }
    }
    this.ws = null;
  }
}
